/**
 * CartPage Component
 */

import * as actions from '../../../redux/actions/cart';
import * as playerActions from '../../../redux/actions/player';
import { pushPostAuthenticationRequestPage } from '../../../redux/actions/postAuthenticationRequests';
import { showSignUpForm } from '../../../redux/actions/ui';
import { generateLicenseUsageFromComponents, getLicenseString } from '../../../redux/helpers/license';
import { getPlayerTracksFromResults } from '../../../redux/helpers/player';
import { cartFragment } from '../../../relay/cart';
import {
  getCartTotal,
  hasValidCart,
} from '../../../relay/selectors/cart';
import { cartLicenseLineItemsAreValid, getCartLineItems } from '../../../relay/selectors/lineItem';
import { ListItemAnimations } from '../../../utils/animation';
import {
  getDefaultTrackAsset,
  getResponsiveTrackThumbnail,
  getTrackResultsFromLineItems,
} from '../../../utils/track';
import { bw6, white } from '../../styles/colors';
import './styles.scss';

import { RouteMatch } from 'found';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';
import { VelocityComponent, VelocityTransitionGroup } from 'velocity-react';

/* tslint:disable-next-line */
import 'velocity-animate/velocity.ui';

import Page from '../../../containers/Page';
import { CurrencyContext } from '../../../contexts/currency';
import { ICurrencyContext } from '../../../contexts/currency/types';
import Button from '../../atoms/buttons/Button';
import LicenseBuilderButton from '../../atoms/buttons/LicenseBuilderButton';
import { IconArrowRight, IconEdit } from '../../atoms/images/Icon';
import Heading from '../../atoms/text/Heading';
import { Span } from '../../atoms/text/Label';
import PriceTag from '../../atoms/text/PriceTag';
import PageHeader from '../../molecules/blocks/PageHeader';
import Card from '../../molecules/media/Card';
import Alert from '../../molecules/messaging/Alert';
import { CheckoutPagePath } from '../Checkout';

// Page Path
export const CartPagePath = '/my/cart';

// Page Props
interface IOwnStateProps {
  license: ITermsState;
  userIsLoggedIn: boolean;
}

interface IOwnDispatchProps {
  removeItemFromCart(lineItem: GQL.ILicenseLineItem): Promise<any>;
  setPlayerQueue(tracks?: IPlayerTrack[]): void;
  signUpAndCheckout(): void;
  updateCartItemTerms(lineItemId: string, terms: ILicenseUsage[]): Promise<any>;
}

interface IOwnProps extends RouteMatch {
  viewer: GQL.IViewer;
}

type CartPageProps = IOwnProps & IOwnStateProps & IOwnDispatchProps;

interface IOwnState {
  hasCart: boolean;
}

const CartItemAnimations = ListItemAnimations(144, -200);

const classes = new BEM('cart');

class CartPage extends React.Component<CartPageProps, IOwnState> {
  static getDerivedStateFromProps(nextProps: CartPageProps, prevState: IOwnState) {
    const nextHasCart = hasValidCart(nextProps.viewer);
    return prevState.hasCart !== nextHasCart ? { hasCart: nextHasCart } : null;
  }

  constructor(props: CartPageProps) {
    super(props);
    this.state = {
      hasCart: hasValidCart(props.viewer),
    };
  }

  componentDidMount() {
    if (this.state.hasCart) {
      this.props.setPlayerQueue();
    }
  }

  onSaveItemLicense = (lineItemId: string) => (usages: ILicenseUsage[]) => {
    this.props.updateCartItemTerms(lineItemId, usages);
  }

  onRemoveItem = (item: GQL.ILicenseLineItem) => () => {
    this.props.removeItemFromCart(item);
  }

  onRequestCheckout = () => {
    if (!this.props.userIsLoggedIn) {
      this.props.signUpAndCheckout();
    } else {
      this.props.router.push(CheckoutPagePath);
    }
  }

  renderLineItem(viewer: GQL.IViewer, lineItem: GQL.ILicenseLineItem) {
    const track = lineItem.track!;
    const trackOriginalVersionId = track.assets && track.assets.length ?
      getDefaultTrackAsset(track.assets!).id : undefined;
    const thumbnail = getResponsiveTrackThumbnail(track);
    const usages = lineItem.license!.usages!.map(({ components }) => generateLicenseUsageFromComponents(components!));
    const licenseString = getLicenseString(usages);
    const options: Array<CardOption | ICardOption> = [
      'PriceElement',
      {
        option: 'EditLicense',
        element: (
          <LicenseBuilderButton
            scope={{
              type: 'Track',
              id: lineItem.id,
            }}
            usages={usages}
            onSaveLicense={this.onSaveItemLicense(lineItem.id)}
            icon={<IconEdit />}
            stopPropagation
          />
        ),
      },
      {
        option: 'Download',
      },
      {
        option: 'RemoveFromCart',
        listener: this.onRemoveItem(lineItem),
      },
    ];
    return (
      <li key={lineItem.id} {...classes('line-item')}>
        {lineItem.amount! === 0 && <div {...classes('line-item__red-dot')} />}
        <Card
          actionSource="cart_page"
          backgroundColor={white}
          trackId={trackOriginalVersionId}
          title={track.title!}
          subtitle={track.artist!.name!}
          image={thumbnail}
          description={licenseString}
          options={options}
          PriceElement={<PriceTag rate={viewer.selected_currency_rate!} amount={lineItem.amount!} size="large" />}
          size="medium"
          trackResult={track}
          alwaysDisplayOptions
          {...classes('card') }
        />
      </li>
    );
  }

  render() {
    const { viewer } = this.props;

    const areLineItemLicensesValid = cartLicenseLineItemsAreValid(viewer);
    const lineItems = getCartLineItems(viewer);
    const cartTotal = getCartTotal(viewer);
    const classModifiers = {
      empty: !this.state.hasCart,
    };

    return (
      <Page id="cart" layout="loose" {...classes('', classModifiers) }>
        <PageHeader title="Your Cart" withCurrencySelect />
        {!!this.state.hasCart ? (
          <>
            <div key="cart-with-items" {...classes('content-wrapper')}>
              <Alert
                visible={!areLineItemLicensesValid}
                message="You cannot proceed to checkout because some tracks don't have a valid license."
              />
              <div {...classes('content')}>
                <VelocityTransitionGroup
                  component="ul"
                  enter={{ animation: CartItemAnimations.In, stagger: 150 }}
                  leave={{ animation: CartItemAnimations.Out }}
                >
                  {lineItems.map((item) => this.renderLineItem(viewer, item))}
                </VelocityTransitionGroup>
              </div>
            </div>
            <VelocityComponent animation={{ translateY: areLineItemLicensesValid ? 0 : '100%' }}>
              <div {...classes('footer')}>
                <div {...classes('footer__content')}>
                  <div>
                    <PriceTag
                      label="Total:"
                      rate={viewer.selected_currency_rate!}
                      amount={cartTotal!}
                      size="large"
                      {...classes('footer__title-total')}
                    />
                    <Span
                      size="small"
                      {...classes('footer__fine-prints')}
                    >Promo code and taxes will be calculated at checkout.
                    </Span>
                    <Span
                      size="small"
                      {...classes('footer__fine-prints')}
                    >We accept credit cards and 30 days check payments.
                    </Span>
                  </div>
                  <div {...classes('footer__content__right')}>
                    <Button
                      onClick={this.onRequestCheckout}
                      icon={<IconArrowRight />}
                      iconPosition="right"
                      label="Proceed to checkout"
                      size="xlarge"
                      colorScheme="accent"
                      variant="pill"
                      disabled={!areLineItemLicensesValid}
                      withShadow
                      {...classes('button-checkout')}
                    />
                  </div>
                </div>
              </div>
            </VelocityComponent>
          </>
        ) : (
            <VelocityComponent animation="transition.slideUpIn" runOnMount>
              <div key="empty-cart" {...classes('content-wrapper')}>
                <div {...classes('content')}>
                  <Heading is="h1" textColor={bw6} {...classes('title-empty')}>Your cart is empty.</Heading>
                </div>
              </div>
            </VelocityComponent>
          )}
      </Page>
    );
  }
}

// tslint:disable-next-line:max-line-length
const ConnectedCartPage = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps & Pick<ICurrencyContext, 'currency'>, CartPageProps, IStoreState>(
  ({ license, user }) => ({
    license,
    userIsLoggedIn: user.isLoggedIn,
  }),
  (dispatch, ownProps) => ({
    removeItemFromCart: (lineItem) =>
      new Promise((resolve, reject) => {
        dispatch(actions.requestRemoveItemFromCart({
          lineItem,
          currencyCode: ownProps.currency,
          promise: { resolve, reject },
        }));
      }),
    setPlayerQueue: (tracks: IPlayerTrack[]) => {
      dispatch(playerActions.replaceQueue({ tracks }));
    },
    signUpAndCheckout: () => {
      dispatch(pushPostAuthenticationRequestPage({ page: CheckoutPagePath }));
      dispatch(showSignUpForm({ reason: 'checkout' }));
    },
    updateCartItemTerms: (lineItemId, terms) =>
      new Promise((resolve, reject) => {
        dispatch(actions.requestUpdateCartItemTerms({
          lineItemId,
          promise: { resolve, reject },
          terms,
        }));
      }),
  }),
  (stateProps, dispatchProps, { currency, ...ownProps }) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    setPlayerQueue: () => {
      dispatchProps.setPlayerQueue(
        getPlayerTracksFromResults(
          getTrackResultsFromLineItems(
            ownProps.viewer.cart!.license_line_items!,
          ),
        ),
      );
    },
  }),
)(CartPage);

const CartPageWithContext: React.SFC<IOwnProps> = (props) => (
  <CurrencyContext.Consumer>
    {({ currency }) => (
      <ConnectedCartPage currency={currency} {...props} />
    )}
  </CurrencyContext.Consumer>
);

const RelayCartPage = createFragmentContainer(
  CartPageWithContext,
  cartFragment,
);

export default RelayCartPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Cart/index.tsx